import { codeUnitString } from "./code-unit-string.js";
import { AUDIT_DETAILS_MAX_BYTES } from "./audit-text-bounds.js";
import { AUDIT_ACTOR_TYPES, AUDIT_LOG_SOURCES, AUDIT_VERB_PATTERN } from "./audit-vocabulary.js";
import { z } from "zod";
//#region src/audit-envelope.ts
/**
* The audit event envelope a producer publishes to `zion.audit.<orgId>.<source>`
* and the archiver turns into one parquet row (feature `audit-sink`, Stage A;
* `00-index.md` 1.2, rulings R-005 and R-012).
*
* Two layers. {@link auditEventEnvelopeV1Schema} is the payload: the clear
* columns of the row plus `enc`, the identifying fields as ciphertext the
* producer made and the archiver copies without reading. {@link auditEnvelopeMessageSchema}
* is the wire message: the `SignedNatsEnvelope` wrapper every signed publisher in
* the tree already emits, with `payload` narrowed to the audit payload. The
* archiver parses the wrapper, keeps `keyId` as the row's `publisher_kid`, and
* verifies the signature from Stage D on; in Stage A the wrapper's `orgId`, the
* payload's `orgId` and the subject's third token must agree, and nothing else is
* checked about the signature.
*
* Both schemas are `.strict()`: an unknown key at any depth is a refusal, and a
* refused message is quarantined, never archived. The caps below bound what a
* single message can make the archiver hold; the broker's `max_payload` (1 MiB on
* the fleet) bounds the whole.
*/
/** The payload version. A breaking change to the payload is a new literal and a new schema. */
const AUDIT_ENVELOPE_VERSION = 1;
/**
* The one id grammar (R-038): lowercase `8-4-4-4-12` hex, the form Postgres emits a
* `uuid` in. Every door uses it: the payload's `eventId`, `orgId` and `actorId`, the
* wrapper's `orgId`, the subject's third token, every object key, and the Rust
* mirror. Upper case, braces, an unhyphenated form and any non-hex nibble are refused
* everywhere, so a payload that parses is always one a key can be built from.
*/
const AUDIT_CANONICAL_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** `true` for a canonical lowercase id; the test every subject and key builder applies first. */
function isAuditCanonicalId(s) {
	return AUDIT_CANONICAL_ID_PATTERN.test(s);
}
/** The zod form of {@link AUDIT_CANONICAL_ID_PATTERN}. */
const auditCanonicalIdSchema = z.string().regex(AUDIT_CANONICAL_ID_PATTERN, "audit id: lowercase 8-4-4-4-12 hex");
/**
* Ceilings on every wrapper field (R-034). `keyId` becomes the row's `publisher_kid`
* and an entry in every seal record, the `.json` the archiver reads back on restart,
* so an unbounded wrapper would size the chain. `payloadJson` is the exact signed
* bytes and may be as large as the broker's 1 MiB `max_payload`.
*/
const AUDIT_WRAPPER_CAPS = {
	signature: 1024,
	signedAt: 64,
	keyId: 256,
	payloadJson: 1048576,
	actorGrant: 8192,
	headerKeys: 32,
	headerKey: 128,
	headerValue: 4096,
	actorId: 128,
	actorOrgId: 128
};
/**
* The largest decoded ciphertext one `enc` field may carry: the 65,536-byte
* `details` cap that `auth.audit_log` already enforces, plus 512 bytes for a
* version byte, a 12-byte IV, a 16-byte GCM tag and any per-scheme header. The
* other three fields are far smaller in practice and share the cap so the schema
* has one number.
*/
const AUDIT_ENC_FIELD_MAX_BYTES = AUDIT_DETAILS_MAX_BYTES + 512;
/**
* The envelope's widest text cap: `objectId`. The reader wire (`AUDIT_READER_MAX_TERM_CHARS`)
* reads it, so a sealed row can never exceed what the row contract admits (R-028).
*/
const AUDIT_ENVELOPE_OBJECT_ID_MAX_CHARS = 512;
/**
* The most data subjects one event may name. The reader row's `data_subjects` list
* (`AUDIT_READER_MAX_ROW_SUBJECTS`) reads it, so a page's subject resolve is bounded (R-057).
*/
const AUDIT_ENVELOPE_MAX_DATA_SUBJECTS = 16;
/** The outcome values `auth.audit_log`'s `audit_log_outcome_check` admits. */
const AUDIT_OUTCOMES = [
	"success",
	"failure",
	"error"
];
/**
* The kinds a data-subject id may name. `user` and `org` are the two the keystore
* mints keys for in Stage B; `principal` and `custom` exist so an emit site that
* declares a subject (AD-5) has a grammar to declare it in.
*/
const AUDIT_DATA_SUBJECT_KINDS = [
	"user",
	"org",
	"principal",
	"custom"
];
const DATA_SUBJECT_RE = /^(user|org|principal|custom):[A-Za-z0-9._-]{1,128}$/;
const BASE64_RE = /^[A-Za-z0-9+/]+={0,2}$/;
/** Decoded byte length of a padded base64 string, or -1 when it is not padded base64. */
function base64DecodedLength(s) {
	if (s.length === 0 || s.length % 4 !== 0 || !BASE64_RE.test(s)) return -1;
	const pad = s.endsWith("==") ? 2 : s.endsWith("=") ? 1 : 0;
	return s.length / 4 * 3 - pad;
}
/**
* One encrypted field: padded base64 of at least one byte (the version byte) and
* at most {@link AUDIT_ENC_FIELD_MAX_BYTES} decoded bytes. The archiver stores
* the decoded bytes in a `BINARY` column and never interprets them.
*/
const auditEncryptedFieldSchema = z.string().refine((s) => {
	const n = base64DecodedLength(s);
	return n >= 1 && n <= AUDIT_ENC_FIELD_MAX_BYTES;
}, `audit enc field: padded base64 of 1 to ${AUDIT_ENC_FIELD_MAX_BYTES} decoded bytes`);
/** `<kind>:<id>`, kind from {@link AUDIT_DATA_SUBJECT_KINDS}, id 1 to 128 chars of `[A-Za-z0-9._-]`. */
const auditDataSubjectIdSchema = z.string().regex(DATA_SUBJECT_RE, "audit data subject id: <kind>:<id>");
/** The four identifying fields, each optional, each ciphertext. Unknown keys are refused. */
const auditEncryptedFieldsSchema = z.object({
	objectDisplay: auditEncryptedFieldSchema.optional(),
	ip: auditEncryptedFieldSchema.optional(),
	userAgent: auditEncryptedFieldSchema.optional(),
	details: auditEncryptedFieldSchema.optional()
}).strict();
/**
* The payload. Field by field:
* - `v`: {@link AUDIT_ENVELOPE_VERSION}.
* - `eventId`: the row's primary key today (`auth.audit_log.id`), the dedupe key
*   everywhere, and the value the relay sets as `Nats-Msg-Id`.
* - `orgId`: canonical UUID; must equal the wrapper's `orgId` and the subject token.
* - `occurredAt`: when the event happened, RFC 3339; `recorded_at` is the server's.
* - `actorId`, `actorType`: the actor as `auth.audit_log` carries it; no display name (AD-3).
* - `action`: the verb, `AUDIT_VERB_PATTERN`, at most 64 chars.
* - `objectType`, `objectId`, `app`, `emitter`, `correlationId`: the clear columns.
* - `relatedObjectId`: optional. The clear id of the second entity the row names, for an event
*   type whose vocabulary entry declares `related` (2026-10-06-audit-second-id-filter, D-2, D-5):
*   a group member, a child group, a role definition, a grantee or a machine principal. The
*   archiver writes it to parquet column 27, `related_object_id`, and the reader matches a
*   `userId` read against it beside `actor_id` and `object_id` (D-1). Only the canonical id
*   grammar is admitted, so the column holds an id and never free text. A producer that holds a
*   declared value outside that grammar omits the key, because a refusal here quarantines the
*   whole event (defaults row 3). An archiver older than revision 3 refuses the key, so the
*   archiver ships before any producer sets it (D-6).
* - `source`: `platform` or `app`; also the subject's fourth token.
* - `outcome`: one of {@link AUDIT_OUTCOMES}.
* - `dataSubjects`: at most `AUDIT_ENVELOPE_MAX_DATA_SUBJECTS` ids whose keys the `enc` fields use.
* - `enc`: {@link auditEncryptedFieldsSchema}.
*/
const auditEventEnvelopeV1Schema = z.object({
	v: z.literal(1),
	eventId: auditCanonicalIdSchema,
	orgId: auditCanonicalIdSchema,
	occurredAt: z.string().datetime(),
	actorId: auditCanonicalIdSchema.optional(),
	actorType: z.enum(AUDIT_ACTOR_TYPES).optional(),
	action: codeUnitString().min(1).max(64).regex(AUDIT_VERB_PATTERN, "audit action: AUDIT_VERB_PATTERN"),
	objectType: codeUnitString().min(1).max(128).optional(),
	objectId: codeUnitString().min(1).max(512).optional(),
	relatedObjectId: auditCanonicalIdSchema.optional(),
	app: codeUnitString().min(1).max(128).optional(),
	source: z.enum(AUDIT_LOG_SOURCES),
	emitter: codeUnitString().min(1).max(64).optional(),
	outcome: z.enum(AUDIT_OUTCOMES).optional(),
	correlationId: codeUnitString().min(1).max(128).optional(),
	dataSubjects: z.array(auditDataSubjectIdSchema).max(16),
	enc: auditEncryptedFieldsSchema
}).strict();
/** The advisory actor block of `SignedNatsEnvelope` (`EventActor`), as a schema. */
const auditEnvelopeActorSchema = z.object({
	type: z.enum([
		"user",
		"agent",
		"worker",
		"service"
	]),
	id: codeUnitString().min(1).max(AUDIT_WRAPPER_CAPS.actorId),
	orgId: codeUnitString().min(1).max(AUDIT_WRAPPER_CAPS.actorOrgId)
}).strict();
/** The NATS headers the publisher set: at most 32 keys of 128 chars with values of 4,096. */
const auditEnvelopeHeadersSchema = z.record(codeUnitString().min(1).max(AUDIT_WRAPPER_CAPS.headerKey), codeUnitString().max(AUDIT_WRAPPER_CAPS.headerValue)).refine((h) => Object.keys(h).length <= AUDIT_WRAPPER_CAPS.headerKeys, `audit envelope: at most ${AUDIT_WRAPPER_CAPS.headerKeys} headers`);
/**
* The wire message: `SignedNatsEnvelope<AuditEventEnvelopeV1>`. The required
* wrapper fields are the ones `isSignedNatsEnvelope` checks; the optional ones are
* the additive fields the publisher may set. `payloadJson`, when present, is the
* exact bytes the producer signed and the bytes Stage D verifies; Stage A stores
* `keyId` and checks nothing cryptographic. The wrapper's `orgId` must equal the
* payload's; the subject check is the archiver's, because the subject is not in
* the message.
*/
const auditEnvelopeMessageSchema = z.object({
	payload: auditEventEnvelopeV1Schema,
	headers: auditEnvelopeHeadersSchema,
	orgId: auditCanonicalIdSchema,
	signature: codeUnitString().min(1).max(AUDIT_WRAPPER_CAPS.signature),
	signedAt: codeUnitString().min(1).max(AUDIT_WRAPPER_CAPS.signedAt),
	keyId: codeUnitString().min(1).max(AUDIT_WRAPPER_CAPS.keyId),
	actor: auditEnvelopeActorSchema.optional(),
	actorGrant: codeUnitString().min(1).max(AUDIT_WRAPPER_CAPS.actorGrant).optional(),
	sigAlg: z.enum([
		"HS256",
		"EdDSA",
		"ES256"
	]).optional(),
	payloadJson: codeUnitString().min(1).max(AUDIT_WRAPPER_CAPS.payloadJson).optional()
}).strict().superRefine((m, ctx) => {
	if (m.orgId !== m.payload.orgId) ctx.addIssue({
		code: "custom",
		path: ["orgId"],
		message: "audit envelope: wrapper orgId must equal payload.orgId"
	});
});
//#endregion
export { AUDIT_CANONICAL_ID_PATTERN, AUDIT_DATA_SUBJECT_KINDS, AUDIT_ENC_FIELD_MAX_BYTES, AUDIT_ENVELOPE_MAX_DATA_SUBJECTS, AUDIT_ENVELOPE_OBJECT_ID_MAX_CHARS, AUDIT_ENVELOPE_VERSION, AUDIT_OUTCOMES, AUDIT_WRAPPER_CAPS, auditCanonicalIdSchema, auditDataSubjectIdSchema, auditEncryptedFieldSchema, auditEncryptedFieldsSchema, auditEnvelopeActorSchema, auditEnvelopeHeadersSchema, auditEnvelopeMessageSchema, auditEventEnvelopeV1Schema, base64DecodedLength, isAuditCanonicalId };

//# sourceMappingURL=audit-envelope.js.map